import { toCamelCase } from './CaseConverter';

export const parseDate = (dateValue, fallback = null) => {
  if (!dateValue) return fallback;
  
  try {
    const date = new Date(dateValue);
    if (isNaN(date.getTime())) return fallback;
    return date.toISOString();
  } catch (error) {
    return fallback;
  }
};

export const parseBoolean = (value, fallback = false) => {
  if (value === null || value === undefined) return fallback;
  if (typeof value === 'boolean') return value;
  if (typeof value === 'string') {
    const lower = value.toLowerCase();
    if (lower === 'true' || lower === '1' || lower === 'yes') return true;
    if (lower === 'false' || lower === '0' || lower === 'no') return false;
  }
  if (typeof value === 'number') return value !== 0;
  return fallback;
};

export const parseInteger = (value, fallback = null) => {
  if (value === null || value === undefined) return fallback;
  const parsed = parseInt(value, 10);
  return isNaN(parsed) ? fallback : parsed;
};

export const parseFloat = (value, fallback = null) => {
  if (value === null || value === undefined) return fallback;
  const parsed = Number.parseFloat(value);
  return isNaN(parsed) ? fallback : parsed;
};

export const ensureString = (value, fallback = '') => {
  if (value === null || value === undefined) return fallback;
  return String(value);
};

export const ensureArray = (value, fallback = []) => {
  if (Array.isArray(value)) return value;
  if (value === null || value === undefined) return fallback;
  return [value];
};

export const coalesce = (...values) => {
  for (const value of values) {
    if (value !== null && value !== undefined) {
      return value;
    }
  }
  return undefined;
};

export const normalizeField = (source, possibleKeys, fallback = null) => {
  if (!source || typeof source !== 'object') return fallback;
  
  for (const key of possibleKeys) {
    if (source[key] !== undefined && source[key] !== null) {
      return source[key];
    }
  }
  
  // Case-insensitive fallback: protects against real API keys that don't
  // match the exact casing assumed by possibleKeys (e.g. ALL-CAPS "ID").
  const sourceKeys = Object.keys(source);
  for (const key of possibleKeys) {
    const lowerKey = key.toLowerCase();
    const matchingKey = sourceKeys.find((k) => k.toLowerCase() === lowerKey);
    if (matchingKey && source[matchingKey] !== undefined && source[matchingKey] !== null) {
      return source[matchingKey];
    }
  }
  
  return fallback;
};

export const createNormalizer = (defaults, fieldMap) => {
  return (raw = {}) => {
    if (!raw || typeof raw !== 'object') {
      return { ...defaults };
    }
    
    const normalized = { ...defaults };
    
    Object.keys(fieldMap).forEach((outputKey) => {
      const possibleKeys = fieldMap[outputKey];
      const value = normalizeField(raw, possibleKeys, defaults[outputKey]);
      normalized[outputKey] = value;
    });
    
    return normalized;
  };
};

export const normalizeArray = (items, normalizer) => {
  if (!Array.isArray(items)) return [];
  return items.map(normalizer);
};

export const deepMerge = (target, source) => {
  const output = { ...target };
  
  if (!source || typeof source !== 'object') {
    return output;
  }
  
  Object.keys(source).forEach((key) => {
    if (source[key] && typeof source[key] === 'object' && !Array.isArray(source[key])) {
      output[key] = deepMerge(target[key] || {}, source[key]);
    } else {
      output[key] = source[key];
    }
  });
  
  return output;
};

export const removeNullish = (obj) => {
  if (!obj || typeof obj !== 'object') return obj;
  
  const cleaned = {};
  
  Object.keys(obj).forEach((key) => {
    if (obj[key] !== null && obj[key] !== undefined) {
      cleaned[key] = obj[key];
    }
  });
  
  return cleaned;
};

export const normalizeCustomFields = (customFields = []) => {
  if (!Array.isArray(customFields)) return [];
  
  return customFields.map((field) => ({
    name: normalizeField(field, ['name', 'Name', 'field_name', 'FieldName'], ''),
    label: normalizeField(field, ['label', 'Label'], ''),
    value: normalizeField(field, ['value', 'Value'], ''),
    type: normalizeField(field, ['type', 'Type', 'field_type', 'FieldType'], 'text'),
    required: parseBoolean(normalizeField(field, ['required', 'Required', 'is_required', 'IsRequired'], false)),
    visible: parseBoolean(normalizeField(field, ['visible', 'Visible', 'is_visible', 'IsVisible'], true)),
    disabled: parseBoolean(normalizeField(field, ['disabled', 'Disabled', 'is_disabled', 'IsDisabled'], false)),
    options: ensureArray(normalizeField(field, ['options', 'Options'], [])),
  }));
};

export default {
  parseDate,
  parseBoolean,
  parseInteger,
  parseFloat: parseFloat,
  ensureString,
  ensureArray,
  coalesce,
  normalizeField,
  createNormalizer,
  normalizeArray,
  deepMerge,
  removeNullish,
  normalizeCustomFields,
};
